import {
    IonCard,
    IonCardContent,
    IonCardSubtitle,
    IonCardTitle,
    IonCol,
    IonItem,
    IonLabel,
    IonList,
    IonRadio,
    IonReorder,
    IonRow,
    isPlatform,
} from "@ionic/react";
import React, { useEffect, useState } from "react";
import { Barn, Horse } from "../../models";
import { getHorsesByBarnId } from "../../utilities/horse/Horse";
import { Table } from "reactstrap";
import AddHorseCard from "./AddHorseCard";
import { calculateHeightInHands } from "../../utilities/horse/Height";
import { useHistory } from "react-router";
import Spinner from "../Spinners/Spinner";
import { useSubscriptionByItself } from "../../utilities/subscription/Subscription";
import { onCreateHorse, onUpdateHorse } from "../../graphql/subscriptions";

interface _Props {
    barn?: Barn
}

const EditHorsesTable: React.FC<_Props> = ({barn}) => {
    const history = useHistory();

    const horseCreateSubscription = useSubscriptionByItself({
        config: {
            query: onCreateHorse,
            key: "onCreateHorse"
        }
    });

    const horseUpdateSubscription = useSubscriptionByItself({
        config: {
            query: onUpdateHorse,
            key: "onUpdateHorse"
        }
    });

    const [previousBarn, setPreviousBarn] = useState<Barn | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [horses, setHorses] = useState<Horse[] | null | undefined>();
    const [currentCreateSubscriptionItem, setCurrentCreateSubscriptionItem] = useState<any>();
    const [currentUpdateSubscriptionItem, setCurrentUpdateSubscriptionItem] = useState<any>();

    const sortHorses = (horses: Horse[]) => {
        return horses.sort((a: Horse, b: Horse) => a.name.localeCompare(b.name));
    }

    useEffect(() => {
        async function getHorses() {
            if (barn) {
                setIsLoading(true);
                const queryResult = await getHorsesByBarnId(barn.id);
                if (queryResult.isSuccess) {
                    const horses = queryResult.result;
                    const sorted = sortHorses(horses);
                    setHorses(sorted);
                }
                setIsLoading(false);
            }
        }

        if ((barn && !horses) || (barn && previousBarn && previousBarn.id !== barn?.id)) {
            getHorses();
            setPreviousBarn(barn);
        }
    }, [barn]);
    
    useEffect(() => {
        if (horseCreateSubscription && horseCreateSubscription[0] !== undefined && horseCreateSubscription[0] !== currentCreateSubscriptionItem) {
            setCurrentCreateSubscriptionItem(horseCreateSubscription[0]);
            const newHorse: any = horseCreateSubscription[0];
            setCurrentCreateSubscriptionItem(newHorse);
            if (newHorse.barnId === barn?.id) {
                handleCreateHorse(newHorse);
            }
        }
    }, [horseCreateSubscription]);

    useEffect(() => {
        if (horseUpdateSubscription && horseUpdateSubscription[0] !== undefined && horseUpdateSubscription[0] !== currentUpdateSubscriptionItem) {
            const updatedHorse: any = horseUpdateSubscription[0];
            setCurrentUpdateSubscriptionItem(updatedHorse);
            if (updatedHorse.barnId === barn?.id) {
                handleEditHorse(updatedHorse);
            }
        }
    }, [horseUpdateSubscription]);

    const onSelectHorse = (horse: Horse) => {
        const path = "/index/staff/barn/horse/" + horse.id;
        history.push(path);
    }

    const handleCreateHorse = (horse: Horse) => {
        let updated: Horse[] = [horse];
        if (horses) {
            updated = updated.concat(horses);
        }
        const sorted = sortHorses(updated);
        setHorses(sorted);
    }

    const handleEditHorse = (updatedHorse: Horse) => {
        if (horses) {
            const index = horses.findIndex(horse => horse.id === updatedHorse.id);
            if (index > -1) {
                const updated = [
                    ...horses.slice(0, index),
                    updatedHorse,
                    ...horses.slice(index + 1)
                ];
                setHorses(updated);
            }
        } else {
            setHorses([updatedHorse]);
        }
    }

    return (
        <IonCard mode="md" className="ion-padding bg-white">
            <IonCardTitle>
                <IonRow>
                    <IonCol sizeXs="12" sizeMd="4">
                        Horses
                    </IonCol>
                    <IonCol offsetMd="4" sizeXs="12" sizeMd="4" className="ion-text-right">
                        <AddHorseCard barn={barn} handleNewHorse={handleCreateHorse} />
                    </IonCol>
                </IonRow>
            </IonCardTitle>
            {isLoading ?
                <Spinner />
                :
                <>
                     <IonCardSubtitle>
                        <IonRow>
                            <IonCol>
                                Click on a Horse record to edit it.
                            </IonCol>
                        </IonRow>
                        <IonRow>
                            <IonCol>
                                Count: {horses ? horses.length : 0}
                            </IonCol>
                        </IonRow>
                    </IonCardSubtitle>
                    <IonCardContent>
                        {isPlatform("mobile") ?
                            <IonList className="bg-white">
                                {(horses && horses.length > 0) && (
                                    <>
                                        {horses.map((horse, index) => (
                                            <IonItem key={index} onClick={() => onSelectHorse(horse)}>
                                                <IonLabel>
                                                    <p className="ion-text-wrap">{horse.name}</p>
                                                </IonLabel>
                                                <IonRadio slot="start" color="tertiary" value={horse.id} />
                                                <IonReorder slot="start" />
                                            </IonItem>
                                        ))}
                                    </>
                                )}
                            </IonList>
                            :
                            <Table hover>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Nickname</th>
                                        <th>Breed</th>
                                        <th>Height</th>
                                        <th>Owner</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(horses && horses.length > 0) && (
                                        <>
                                            {horses.map((horse, index) => (
                                                <tr key={index} onClick={() => onSelectHorse(horse)}>
                                                    <td>
                                                        {horse.name}
                                                    </td>
                                                    <td>
                                                        {horse.nickname}
                                                    </td>
                                                    <td>
                                                        {horse.breed}
                                                    </td>
                                                    <td>
                                                        {(horse.height && horse.height !== "0") ? (calculateHeightInHands(parseInt(horse.height)) + (horse.fraction ? (" " + horse.fraction) : "")) : ""}
                                                    </td>
                                                    <td>
                                                        {horse.ownerName}
                                                    </td>
                                                </tr>
                                            ))}
                                        </>
                                    )}
                                </tbody>
                            </Table>
                        }
                    </IonCardContent>
                </>
            }
        </IonCard>
    );
};

export default EditHorsesTable;